import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Plus, Search, Trash2, X, Package, Clock3, Crown, PackageOpen, Layers } from 'lucide-react';
import styles from './InvoiceGrid.module.css';
import { PRICE_MODES, calculatePricing } from '../../../../utils/pricingUtils';
import { useStore } from '../../../../context/StoreContext';
import useAccess from '../../../../hooks/useAccess';

// Catalog is cached per store for a few minutes, so opening / creating bills does not re-download 6 lists every time
const CATALOG_TTL_MS = 5 * 60 * 1000;
const catalogCache = new Map(); // storeId -> { at, catalog, staff }

const asPeopleArray = (value) => {
    if (!value) return [];
    const arr = Array.isArray(value) ? value : [value];
    return arr.map((person) => {
        if (typeof person === 'string') return { staffId: null, name: person.trim() };
        if (typeof person === 'object' && person !== null) return { staffId: person.staffId || null, name: String(person.name || '').trim() };
        return null;
    }).filter((p) => p && p.name);
};

const readJson = async (res) => (res?.ok ? res.json().catch(() => null) : null);

const fetchCatalog = async (catalogApiBase, storeId) => {
    const token = localStorage.getItem('smiify_token');
    const requestOptions = { headers: { Authorization: `Bearer ${token}`, 'x-store-id': storeId } };

    const [servicesRes, inventoryRes, packagesRes, prepaidRes, discountRes, staffRes] = await Promise.all([
        fetch(`${catalogApiBase}/services?storeId=${storeId}`, requestOptions).catch(() => null),
        fetch(`${catalogApiBase}/inventory?usage=pos`, requestOptions).catch(() => null),
        fetch(`${catalogApiBase}/packages/pos/package?storeId=${storeId}`, requestOptions).catch(() => null),
        fetch(`${catalogApiBase}/prepaid-memberships/pos/prepaid?storeId=${storeId}`, requestOptions).catch(() => null),
        fetch(`${catalogApiBase}/discount-memberships/pos/discount?storeId=${storeId}`, requestOptions).catch(() => null),
        fetch(`${catalogApiBase}/staff`, requestOptions).catch(() => null),
    ]);

    const [sResult, iResult, pResult, preResult, discResult, stResult] = await Promise.all([
        readJson(servicesRes), readJson(inventoryRes), readJson(packagesRes), readJson(prepaidRes), readJson(discountRes), readJson(staffRes),
    ]);

    const catalog = [];

    if (sResult?.success && Array.isArray(sResult.data)) {
        sResult.data.filter((s) => s.status === 'Active' || s.isActive !== false).forEach((s) => {
            const baseServicePrice = Number(s.price || s.sellingPrice || 0);
            let absoluteDiscAmt = 0; let discType = '%'; let discVal = '';

            if (s.discount?.isActive && s.discount?.value) {
                const dVal = Number(s.discount.value);
                if (s.discount.type === 'Percentage' || s.discount.type === '%') {
                    discType = '%'; discVal = dVal;
                    absoluteDiscAmt = (baseServicePrice * dVal) / 100;
                } else {
                    discType = '₹'; discVal = dVal; absoluteDiscAmt = dVal;
                }
            }

            const itemType = s.serviceType || s.type || 'Service';
            catalog.push({
                id: s.id || s._id, name: s.name, type: 'service', displayType: itemType.toLowerCase().includes('combo') ? 'Combo' : 'Service',
                category: s.category || 'Service', rate: baseServicePrice, discountValue: discVal, discountType: discType,
                hsn: s.hsn || '9982', duration: Number(s.duration || 0), gstRate: s.gstRate ?? s.taxPercent ?? 0,
                priceMode: s.priceMode || (s.priceIncludesGst ? PRICE_MODES.INCLUSIVE : PRICE_MODES.EXCLUSIVE), maxQty: null,
            });

            if (Array.isArray(s.addons) && s.addons.length > 0) {
                s.addons.forEach((addon) => {
                    const addonPrice = Number(addon.price || addon.effectivePrice || 0);
                    catalog.push({
                        id: addon._id || addon.id, name: `${s.name} + ${addon.name}`, type: 'service', displayType: 'Add-on',
                        category: `Includes ${s.name}`, rate: baseServicePrice + addonPrice, discountValue: absoluteDiscAmt > 0 ? absoluteDiscAmt : '',
                        discountType: '₹', hsn: s.hsn || '9982', duration: Number(s.duration || 0) + Number(addon.duration || 0),
                        gstRate: s.gstRate ?? 0, priceMode: s.priceMode || (s.priceIncludesGst ? PRICE_MODES.INCLUSIVE : PRICE_MODES.EXCLUSIVE), maxQty: null,
                    });
                });
            }
        });
    }

    if (iResult?.success && Array.isArray(iResult.data)) {
        catalog.push(
            ...iResult.data
                .filter((p) => p.isActive && ['RETAIL', 'HYBRID'].includes(p.usageType) && p.stock?.currentQuantity > 0)
                .map((p) => ({
                    id: p._id, name: p.basicInfo?.productName || 'Product', type: 'product', displayType: 'Product', category: p.basicInfo?.category || 'Retail',
                    rate: p.pricing?.sellingPrice || p.pricing?.mrp || 0, discountValue: '', discountType: '%',
                    hsn: p.pricing?.hsnCode || '3304', duration: 0, gstRate: p.pricing?.taxRate ?? 0,
                    priceMode: PRICE_MODES.EXCLUSIVE, maxQty: p.stock?.currentQuantity ?? 0,
                }))
        );
    }

    if (pResult?.success && Array.isArray(pResult.data)) {
        catalog.push(...pResult.data.map((pkg) => ({
            id: pkg.id, name: pkg.name, type: 'package', displayType: 'Package', category: 'Bundles',
            rate: pkg.netPayable || 0, discountValue: '', discountType: '%',
            hsn: '9982', duration: 0, gstRate: pkg.gstRate ?? 0, priceMode: PRICE_MODES.EXCLUSIVE, maxQty: 1,
        })));
    }

    if (preResult?.success && Array.isArray(preResult.data)) {
        catalog.push(...preResult.data.map((mem) => ({
            id: mem.id, name: mem.name, type: 'prepaid', displayType: 'Prepaid', category: 'Prepaid Wallet',
            rate: mem.price || 0, discountValue: '', discountType: '%',
            hsn: '9982', duration: 0, gstRate: mem.gstRate ?? 0, priceMode: PRICE_MODES.EXCLUSIVE, maxQty: 1, walletCredit: mem.credit,
        })));
    }

    if (discResult?.success && Array.isArray(discResult.data)) {
        catalog.push(...discResult.data.map((mem) => ({
            id: mem.id, name: mem.name, type: 'discount', displayType: 'Discount', category: 'VIP Pass',
            rate: mem.price || 0, discountValue: '', discountType: '%',
            hsn: '9982', duration: 0, gstRate: mem.gstRate ?? 0, priceMode: PRICE_MODES.EXCLUSIVE, maxQty: 1,
            serviceDiscount: mem.serviceDiscount || 0, productDiscount: mem.productDiscount || 0,
        })));
    }

    const staff = stResult?.success && Array.isArray(stResult.data)
        ? stResult.data.map((st) => ({ staffId: st._id, name: st.name }))
        : [];

    return { catalog, staff };
};

const InvoiceGrid = ({ items = [], setItems, getEmptyRow, totals, catalogApiBase, pricingSettings, isPrepaidActive, appliedMembership }) => {
    const { activeStore } = useStore();
    const { can } = useAccess();
    // Live access (the server checks the same rules)
    const canOverridePrice = can('billing.override_price');
    const canLineDiscount = can('billing.apply_discount');
    const canChangeGst = can('pricelist.manage_taxes');
    const canSellPackage = can('packages.sell_package');
    const canSellPlans = can('memberships.sell_membership');
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [searchValues, setSearchValues] = useState({});

    const storeId = activeStore?.id || activeStore?._id;
    const cached = storeId ? catalogCache.get(storeId) : null;
    const [serviceCatalog, setServiceCatalog] = useState(cached?.catalog || []);
    const [staffList, setStaffList] = useState(cached?.staff || []);
    const [isLoading, setIsLoading] = useState(!cached);

    const [focusedRateId, setFocusedRateId] = useState(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (!storeId || !catalogApiBase) return undefined;
        let isMounted = true;

        const hit = catalogCache.get(storeId);
        if (hit) {
            setServiceCatalog(hit.catalog);
            setStaffList(hit.staff);
            setIsLoading(false);
            if (Date.now() - hit.at < CATALOG_TTL_MS) return () => { isMounted = false; };
        } else {
            setIsLoading(true);
        }

        // Fresh copy in the background (or first load)
        (async () => {
            try {
                const result = await fetchCatalog(catalogApiBase, storeId);
                catalogCache.set(storeId, { at: Date.now(), ...result });
                if (!isMounted) return;
                setServiceCatalog(result.catalog);
                setStaffList(result.staff);
            } catch (error) {
                console.error('Catalog load failed:', error);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        })();

        return () => { isMounted = false; };
    }, [storeId, catalogApiBase]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const isClickInsideInput = event.target.closest(`.${styles.itemControl}`) || event.target.closest(`.${styles.peopleControl}`);
            const isClickInsideDropdown = event.target.closest(`.${styles.autocompleteDropdown}`);
            if (!isClickInsideInput && !isClickInsideDropdown) setActiveDropdown(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleItemChange = (id, field, value) => {
        setItems((current) => current.map((item) => {
            if (item.id !== id) return item;
            if (field === 'qty' && item.maxQty !== null && item.maxQty !== undefined) {
                const requestedQty = Number(value) || 0;
                if (requestedQty > item.maxQty) return { ...item, qty: item.maxQty };
            }
            // A discount typed by staff is never overwritten by a membership change
            if (field === 'discountValue' || field === 'discountType') {
                return { ...item, [field]: value, discountSource: 'manual' };
            }
            return { ...item, [field]: value };
        }));
    };

    const handlePeopleToggle = (rowId, field, personObj) => {
        setItems((current) => current.map((item) => {
            if (item.id !== rowId) return item;
            const selected = asPeopleArray(item[field]);
            const exists = selected.some((p) => p.name === personObj.name);
            const next = exists ? selected.filter((p) => p.name !== personObj.name) : [...selected, personObj];
            return { ...item, [field]: next };
        }));
    };

    const removePerson = (rowId, field, personName) => {
        setItems((current) => current.map((item) => (item.id === rowId ? {
            ...item, [field]: asPeopleArray(item[field]).filter((p) => p.name !== personName),
        } : item)));
    };

    const setQuery = (key, value) => setSearchValues((current) => ({ ...current, [key]: value }));
    const handleAddRow = () => setItems((current) => [...current, getEmptyRow()]);
    const handleRemoveRow = (id) => { if (items.length > 1) setItems((current) => current.filter((item) => item.id !== id)); };

    const renderPeopleField = (item, field, type, placeholder) => {
        const key = `${item.id}-${type}`;
        const selected = asPeopleArray(item[field]);
        const selectedNames = selected.map((s) => s.name);
        const query = searchValues[key] || '';
        const matches = staffList.filter((person) => !selectedNames.includes(person.name) && person.name.toLowerCase().includes(query.toLowerCase()));

        return (
            <div className={styles.peopleControl}>
                <div className={styles.peopleChips}>
                    {selected.map((p) => (
                        <span className={styles.personChip} key={p.name}>
                            {p.name}
                            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => removePerson(item.id, field, p.name)}><X size={11} /></button>
                        </span>
                    ))}
                    <div className={styles.inputWithArrow}>
                        <input type="text" placeholder={selected.length ? 'Add...' : placeholder} value={query} onChange={(e) => { setQuery(key, e.target.value); setActiveDropdown(key); }} onFocus={() => setActiveDropdown(key)} className={styles.peopleInput} />
                        {selected.length === 0 && <ChevronDown size={13} className={styles.controlChevron} />}
                    </div>
                </div>
                {activeDropdown === key && (
                    <div className={`${styles.autocompleteDropdown} ${styles.staffDropdown}`} role="listbox">
                        {matches.length ? matches.map((person) => (
                            <button type="button" role="option" aria-selected={false} className={styles.acItem} key={person.name} onMouseDown={(e) => e.preventDefault()} onClick={() => handlePeopleToggle(item.id, field, person)}>
                                <div className={styles.staffDropdownItem}>
                                    <div className={styles.staffAvatar}>{person.name.charAt(0).toUpperCase()}</div>
                                    <strong>{person.name}</strong>
                                </div>
                            </button>
                        )) : <div className={styles.noMatch}>No staff found</div>}
                    </div>
                )}
            </div>
        );
    };

    // Membership chosen on this bill (most expensive by default, staff can change it in the summary)
    const vipServiceDisc = Number(appliedMembership?.serviceDiscount) || 0;
    const vipProductDisc = Number(appliedMembership?.productDiscount) || 0;
    const currentVipPrice = Number(appliedMembership?.price) || 0;

    return (
        <section className={styles.gridSection} ref={dropdownRef} aria-label="Invoice items">
            <div className={styles.tableScroll}>
                <table className={styles.excelTable}>
                    <thead><tr>
                        <th className={styles.colNum}>#</th><th className={styles.colItem}>Item / Service</th><th className={styles.colStaff}>Served By</th><th className={styles.colConsult}>Consulted By</th><th className={styles.colQty}>Qty</th><th className={styles.colRate}>Rate</th><th className={styles.colDisc}>Discount</th><th className={styles.colGst}>GST</th><th className={styles.colAmt}>Amount</th><th className={styles.colAction} />
                    </tr></thead>
                    <tbody>
                        {items.map((item, index) => {
                            const rowData = totals?.rows?.find((r) => r.id === item.id);
                            const pricing = rowData?.pricing || calculatePricing({
                                price: item.rate, priceMode: item.priceMode, gstRate: item.gstRate || 0, gstApplicable: (item.gstRate || 0) > 0, qty: item.qty,
                                discountActive: Number(item.discountValue) > 0, discountType: item.discountType === '₹' ? 'Flat' : 'Percentage', discountValue: item.discountValue,
                            });

                            const effectiveGstRate = pricing.gstRate;
                            const effectivePriceMode = pricing.priceMode;

                            const isProduct = String(item.itemType || '').toLowerCase() === 'product';
                            const isLoyalty = ['package', 'prepaid', 'discount', 'membership'].includes(String(item.itemType || '').toLowerCase());
                            const isServiceLike = !isProduct && !isLoyalty;
                            const isGstDisabled = isServiceLike && isPrepaidActive; // prepaid-paid services: no GST

                            const alreadySelectedNames = items.filter((i) => i.id !== item.id).map((i) => i.itemName);
                            const itemMatches = serviceCatalog.filter((catalogItem) => {
                                if (alreadySelectedNames.includes(catalogItem.name)) return false;
                                if (catalogItem.type === 'package' && !canSellPackage) return false;
                                if (['prepaid', 'discount'].includes(catalogItem.type) && !canSellPlans) return false;
                                if (!catalogItem.name.toLowerCase().includes(String(item.itemName || '').toLowerCase())) return false;
                                // Only higher discount plans are offered when one is already active
                                if (appliedMembership && catalogItem.type === 'discount' && catalogItem.rate <= currentVipPrice) return false;
                                return true;
                            });

                            const itemKey = `${item.id}-item`;
                            // Line paid by a client package: free here, everything locked (use "Use" in the header to add more)
                            const fromPackage = Boolean(item.packageRedeem?.packageId);
                            const isRateFocused = focusedRateId === item.id;
                            const displayRate = isRateFocused
                                ? (item.rate ?? '')
                                : (effectivePriceMode === PRICE_MODES.INCLUSIVE && effectiveGstRate > 0 ? pricing.taxableUnitPrice.toFixed(2) : (item.rate ?? ''));

                            return (
                                <tr key={item.id}>
                                    <td className={styles.rowNum}>{String(index + 1).padStart(2, '0')}</td>

                                    <td className={`${styles.relativeCell} ${styles.colItem}`}>
                                        <div className={styles.itemControl}>
                                            <Search size={13} className={styles.searchIcon} />
                                            <input type="text" placeholder="Search service, product, package..." value={item.itemName || ''} readOnly={fromPackage} onChange={(event) => { if (!fromPackage) handleItemChange(item.id, 'itemName', event.target.value); }} onFocus={() => { if (!fromPackage) setActiveDropdown(itemKey); }} className={styles.cellInput} />
                                            <ChevronDown size={13} className={styles.controlChevron} />
                                        </div>
                                        {fromPackage && (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 2, fontSize: 10, fontWeight: 800, color: 'var(--h-color-success-600, #007a49)' }}>
                                                <PackageOpen size={10} /> Package: {item.packageRedeem.packageName || 'client package'} (no charge)
                                            </span>
                                        )}
                                        {item.discountSource === 'membership' && (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 2, fontSize: 10, fontWeight: 800, color: 'var(--h-color-primary-600, #673de6)' }}>
                                                <Crown size={10} /> {appliedMembership?.name || 'Membership'} discount
                                            </span>
                                        )}
                                        {activeDropdown === itemKey && (
                                            <div className={`${styles.autocompleteDropdown} ${styles.itemDropdown}`} role="listbox">
                                                {isLoading && !serviceCatalog.length && <div className={styles.noMatch}>Loading catalog...</div>}
                                                {itemMatches.length > 0 ? itemMatches.map((catalogItem) => {
                                                    const originalBaseRate = catalogItem.rate ?? 0;
                                                    const catDiscVal = Number(catalogItem.discountValue) || 0;
                                                    const catDiscType = catalogItem.discountType || '%';
                                                    const priceAfterCatalogDisc = catDiscVal > 0
                                                        ? (catDiscType === '%' ? originalBaseRate - ((originalBaseRate * catDiscVal) / 100) : originalBaseRate - catDiscVal)
                                                        : originalBaseRate;

                                                    const lowType = String(catalogItem.displayType || catalogItem.type).toLowerCase();
                                                    let ItemIcon = Clock3;
                                                    if (lowType === 'product') ItemIcon = Package;
                                                    if (lowType === 'add-on' || lowType === 'addon') ItemIcon = Plus;
                                                    if (lowType === 'combo') ItemIcon = Layers;
                                                    if (lowType === 'package') ItemIcon = PackageOpen;
                                                    if (lowType === 'prepaid' || lowType === 'discount') ItemIcon = Crown;

                                                    return (
                                                        <button type="button" className={styles.acItem} key={catalogItem.id} onMouseDown={(event) => event.preventDefault()} onClick={() => {
                                                            const isServiceLikeCat = ['service', 'combo', 'add-on', 'addon'].includes(lowType) || catalogItem.type === 'service';
                                                            const isProductLikeCat = lowType === 'product' || catalogItem.type === 'product';

                                                            let finalDiscVal = '';
                                                            let discountSource;
                                                            if (isServiceLikeCat && vipServiceDisc > 0) { finalDiscVal = vipServiceDisc; discountSource = 'membership'; }
                                                            else if (isProductLikeCat && vipProductDisc > 0) { finalDiscVal = vipProductDisc; discountSource = 'membership'; }

                                                            let gst = catalogItem.gstRate ?? (catalogItem.type === 'product' ? 0 : pricingSettings?.gstRate ?? 0);
                                                            if (['package', 'prepaid', 'discount'].includes(lowType)) gst = catalogItem.gstRate || 0;

                                                            const mode = ['product', 'package', 'prepaid', 'discount'].includes(catalogItem.type)
                                                                ? PRICE_MODES.EXCLUSIVE
                                                                : pricingSettings?.priceMode || PRICE_MODES.EXCLUSIVE;

                                                            setItems((current) => {
                                                                const updatedRows = current.map((row) => (row.id === item.id ? {
                                                                    ...row, itemId: catalogItem.id, itemName: catalogItem.name, itemType: catalogItem.type, maxQty: catalogItem.maxQty,
                                                                    rate: Math.max(0, priceAfterCatalogDisc), discountValue: finalDiscVal, discountType: '%', discountSource,
                                                                    hsn: catalogItem.hsn || '', gstRate: gst, taxPercent: gst, priceMode: mode, duration: catalogItem.duration,
                                                                } : row));
                                                                if (current[current.length - 1].id === item.id) updatedRows.push(getEmptyRow());
                                                                return updatedRows;
                                                            });
                                                            setActiveDropdown(null);
                                                        }}>
                                                            <span>
                                                                <strong>{catalogItem.name}</strong>
                                                                <small>
                                                                    <span className={`${styles.typeBadge} ${lowType === 'product' ? styles.typeBadgeProduct : lowType.includes('add') || lowType.includes('combo') ? styles.typeBadgeAddon : ['package', 'prepaid', 'discount'].includes(lowType) ? styles.typeBadgeLoyalty : ''}`} style={{ textTransform: 'uppercase' }}>
                                                                        <ItemIcon size={10} /> {catalogItem.displayType || catalogItem.type}
                                                                    </span>
                                                                    {catalogItem.category} {catalogItem.maxQty !== null && catalogItem.maxQty !== undefined ? `| ${catalogItem.maxQty} in stock` : ''}
                                                                    {catalogItem.walletCredit > 0 && <span style={{ marginLeft: '6px', color: '#16a34a', fontWeight: 'bold' }}>| Gets ₹{catalogItem.walletCredit} credit</span>}
                                                                </small>
                                                            </span>
                                                            <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                                <b style={{ color: '#18181a' }}>₹{Number(priceAfterCatalogDisc).toLocaleString('en-IN')}</b>
                                                                {catDiscVal > 0 && (
                                                                    <span style={{ fontSize: '9.5px', color: '#9d9ea4', textDecoration: 'line-through' }}>₹{Number(originalBaseRate).toLocaleString('en-IN')}</span>
                                                                )}
                                                            </div>
                                                        </button>
                                                    );
                                                }) : (!isLoading || serviceCatalog.length > 0) && <div className={styles.noMatch}>No item found</div>}
                                            </div>
                                        )}
                                    </td>

                                    <td className={`${styles.relativeCell} ${styles.colStaff}`}>{renderPeopleField(item, 'staff', 'served', 'Select staff')}</td>
                                    <td className={`${styles.relativeCell} ${styles.colConsult}`}>{renderPeopleField(item, 'consultedBy', 'consulted', 'Select consultant')}</td>

                                    <td className={styles.colQty}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <input type="number" min="1" max={item.maxQty || ''} value={item.qty ?? ''} readOnly={fromPackage} title={fromPackage ? 'Use the package "Use" button to add more' : undefined} onChange={(event) => { if (!fromPackage) handleItemChange(item.id, 'qty', event.target.value); }} className={`${styles.cellInput} ${styles.textCenter}`} />
                                            {item.maxQty !== null && item.maxQty !== undefined && <span style={{ fontSize: '8px', color: '#dc2626', fontWeight: 800 }}>Max: {item.maxQty}</span>}
                                        </div>
                                    </td>

                                    <td className={styles.colRate}>
                                        <input type="number" min="0" value={displayRate} readOnly={fromPackage || (Boolean(item.itemId) && !canOverridePrice)} title={fromPackage ? 'Paid in the package' : Boolean(item.itemId) && !canOverridePrice ? 'You cannot change the catalogue price' : undefined} onFocus={() => setFocusedRateId(item.id)} onBlur={() => setFocusedRateId(null)} onChange={(event) => { if (fromPackage || (item.itemId && !canOverridePrice)) return; handleItemChange(item.id, 'rate', event.target.value); }} className={`${styles.cellInput} ${styles.textRight}`} />
                                    </td>

                                    <td className={`${styles.relativeCell} ${styles.colDisc}`}>
                                        <div className={styles.compositeWrapper}>
                                            <input type="number" min="0" value={item.discountValue ?? ''} disabled={!canLineDiscount || fromPackage} title={canLineDiscount ? undefined : 'You cannot give discounts'} onChange={(event) => handleItemChange(item.id, 'discountValue', event.target.value)} className={styles.cInput} />
                                            <select value={item.discountType || '%'} disabled={!canLineDiscount || fromPackage} onChange={(event) => handleItemChange(item.id, 'discountType', event.target.value)} className={styles.cSelect}>
                                                <option value="%">%</option><option value="₹">₹</option>
                                            </select>
                                        </div>
                                    </td>

                                    <td className={styles.colGst}>
                                        <div className={styles.gstControl}>
                                            <select
                                                value={effectiveGstRate}
                                                onChange={(event) => setItems((current) => current.map((row) => (row.id === item.id ? { ...row, gstRate: Number(event.target.value), taxPercent: Number(event.target.value) } : row)))}
                                                className={styles.cellSelect}
                                                disabled={fromPackage || isGstDisabled || !canChangeGst}
                                                title={fromPackage ? 'No GST: it was charged when the package was bought' : isGstDisabled ? 'Services paid by prepaid card have no GST' : !canChangeGst ? 'GST comes from the price list' : 'Select GST rate'}
                                            >
                                                <option value="0">0%</option>
                                                <option value="5">5%</option>
                                                <option value="12">12%</option>
                                                <option value="18">18%</option>
                                                <option value="28">28%</option>
                                            </select>
                                        </div>
                                    </td>

                                    <td className={`${styles.rowAmount} ${styles.colAmt}`}>₹{pricing.finalAmount.toFixed(2)}</td>
                                    <td className={`${styles.rowAction} ${styles.colAction}`}><button type="button" aria-label="Remove line" className={styles.deleteRowBtn} onClick={() => handleRemoveRow(item.id)}><Trash2 size={14} /></button></td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr className={styles.tfootRow}>
                            <td colSpan="4" className={styles.tfootLabel}>Totals</td>
                            <td className={styles.tfootValueCenter}>{totals?.totalQty || 0}</td>
                            <td className={styles.tfootDesc}>₹{totals?.sumRate?.toFixed(2) || '0.00'}</td>
                            <td className={styles.tfootDesc}>₹{totals?.sumItemDiscount?.toFixed(2) || '0.00'}</td>
                            <td className={styles.tfootDesc}>₹{totals?.sumTax?.toFixed(2) || '0.00'}</td>
                            <td className={styles.tfootAmount}>₹{totals?.sumRowAmount?.toFixed(2) || '0.00'}</td>
                            <td />
                        </tr>
                    </tfoot>
                </table>
            </div>
            <button type="button" className={styles.addRowBtn} onClick={handleAddRow}><Plus size={15} /> Add blank line</button>
        </section>
    );
};

export default InvoiceGrid;